import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, FilterX } from "lucide-react";
import { useAuth } from "../AuthContext";
import api, { fmtDate } from "../api";
import StatCards from "../components/StatCards";
import { Card, PageHeader, Input, Select, Button, Table, EmptyState, Spinner, StatusBadge } from "../components/ui";

export default function PandiDashboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [clientId, setClientId] = useState("");
  const [clinicId, setClinicId] = useState("");
  const [clients, setClients] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/clinics").then((r) => setClinics(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user.role !== "pandi") return;
    const fetchStats = () => api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
    fetchStats();
    const t = setInterval(fetchStats, 15000);
    return () => clearInterval(t);
  }, [user.role]);

  useEffect(() => {
    const params = {};
    if (search.trim().length >= 2) params.search = search.trim();
    if (status) params.status = status;
    if (clientId) params.client_id = clientId;
    if (clinicId) params.clinic_id = clinicId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (user.role === "admin") params.has_pandi = true;
    const fetchRows = () => api.get("/candidates", { params }).then((r) => {
      const sorted = [...r.data].sort((a, b) => {
        const aDate = new Date(a.visit_date || a.updated_at || a.created_at || 0).getTime();
        const bDate = new Date(b.visit_date || b.updated_at || b.created_at || 0).getTime();
        return bDate - aDate;
      });
      setRows(sorted);
    }).catch(() => setRows([]));
    const t = setTimeout(fetchRows, 300);
    const iv = setInterval(fetchRows, 15000);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, [search, status, clientId, clinicId, dateFrom, dateTo, user.role]);

  return (
    <div data-testid="pandi-dashboard-page">
      <PageHeader title="P&I Dashboard" subtitle="Candidates covered under P&I clubs — every visit listed with its vessel" />
      {user.role === "pandi" && (
        <div className="mb-6">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400" data-testid="pandi-stats-daily-note">
            Today's figures — counters reset every day at midnight
          </p>
          <StatCards stats={stats} />
        </div>
      )}
      <Card className="mb-6 p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-8">
          <div className="relative lg:col-span-2">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search name / INDOS / mobile / company ID" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pandi-search-input" />
          </div>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pandi-status-filter">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="fit">Accepted</option>
            <option value="temp_unfit">Further Evaluation Required</option>
            <option value="unfit">Not Recommended</option>
          </Select>
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="pandi-client-filter">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select value={clinicId} onChange={(e) => setClinicId(e.target.value)} data-testid="pandi-clinic-filter">
            <option value="">All clinics</option>
            {clinics.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="pandi-date-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="pandi-date-to" />
          <Button variant="outline" onClick={() => { setSearch(""); setStatus(""); setClientId(""); setClinicId(""); setDateFrom(""); setDateTo(""); }} data-testid="pandi-clear-filters-btn">
            <FilterX size={15} /> Clear
          </Button>
        </div>
      </Card>
      <Card>
        {rows === null ? <div className="flex justify-center py-12"><Spinner /></div>
          : rows.length === 0 ? <EmptyState text="No P&I candidates yet" />
          : (
            <Table headers={["Candidate", "Company ID", "Rank", "Client", "Vessel", "Clinic", "Status", "Date", ""]}>
              {rows.map((c) => (
                <tr key={c.visit_id || c.id} className="border-b border-slate-100 transition-colors hover:bg-slate-50" data-testid="pandi-row">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-900">{c.name}</p>
                    {c.indos_number && <p className="text-xs text-slate-500">INDOS {(c.indos_number || "").toUpperCase()}</p>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.company_id || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.rank}</td>
                  <td className="px-4 py-3 text-slate-600">{c.client_name || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{(c.vessel || "-").toUpperCase()}</td>
                  <td className="px-4 py-3 text-slate-600">{c.clinic_name || "-"}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(c.updated_at)}</td>
                  <td className="px-4 py-3">
                    <Link to={`/candidates/${c.id}`} className="text-sm font-semibold text-teal transition-colors hover:text-navy" data-testid={`pandi-open-${c.visit_id || c.id}`}>Open</Link>
                  </td>
                </tr>
              ))}
            </Table>
          )}
      </Card>
    </div>
  );
}
